import { COUNTRY_CODES } from "../constants/countryCodes";
import type { UserWithArtists } from "../services/api/user.service";

type ValidationInput = {
  username: string;
  firstName: string;
  lastName: string;
  artistName: string;
  spotifyUrl: string;
  mobile: string;
  address: string;
};

type PayloadInput = {
  profile: {
    username: string;
    firstName: string;
    lastName: string;
    artistName: string;
    spotifyUrl: string;
    distributor: string;
    mobile: string;
    address: string;
    country: string;
    state: string;
    city: string;
    street: string;
    zipCode: string;
    shirtSize: string;
    youtubeUrl: string;
    appleUrl: string;
    instagramUrl: string;
    facebookUrl: string;
    tiktokUrl: string;
    soundcloudUrl: string;
    twitterUrl: string;
    amazonMusicUrl: string;
    deezerUrl: string;
    audiomackUrl: string;
    pandoraUrl: string;
    tidalUrl: string;
    profilePic: string;
  };
  distributors: string[];
  countryCode: string;
};

const getPrimaryArtist = (data: UserWithArtists) =>
  data.artists?.find((artist) => artist.is_primary) || data.artists?.[0];

const splitMobileWithCountryCode = (mobile: string): { countryCode: string; mobile: string } => {
  if (!mobile) return { countryCode: "", mobile: "" };
  if (!mobile.startsWith("+")) {
    return { countryCode: "", mobile };
  }

  const allDigits = mobile.substring(1).replace(/\D/g, "");
  if (!allDigits) return { countryCode: "", mobile };

  const countryCodesByLength = [3, 2, 1];
  for (const codeLength of countryCodesByLength) {
    if (allDigits.length <= codeLength) continue;
    const candidate = `+${allDigits.substring(0, codeLength)}`;
    const found = COUNTRY_CODES.some((cc) => cc.code === candidate);
    if (found) {
      return {
        countryCode: candidate,
        mobile: allDigits.substring(codeLength),
      };
    }
  }

  // Fallback: preserve original if no known code is matched.
  return { countryCode: "", mobile };
};

export const mapAdminProfileForValidation = (data: UserWithArtists): ValidationInput => {
  const primary = getPrimaryArtist(data);
  return {
    username: data.username || "",
    firstName: data.firstname || "",
    lastName: data.lastname || "",
    artistName: primary?.artist_name || "",
    spotifyUrl: primary?.spotify_url || "",
    mobile: data.mobile || "",
    address: data.address || "",
  };
};

export const mapAdminProfileForPayload = (data: UserWithArtists): PayloadInput => {
  const primary = getPrimaryArtist(data);
  const distributorList = (primary?.distributor || "")
    .split(",")
    .map((d) => d.trim())
    .filter((d) => d.length > 0);
  const normalizedMobile = splitMobileWithCountryCode(data.mobile || "");

  return {
    profile: {
      username: data.username || "",
      firstName: data.firstname || "",
      lastName: data.lastname || "",
      artistName: primary?.artist_name || "",
      spotifyUrl: primary?.spotify_url || "",
      distributor: "",
      mobile: normalizedMobile.mobile,
      address: data.address || "",
      country: data.country || "",
      state: data.state || "",
      city: data.city || "",
      street: data.street || "",
      zipCode: data.zip_code || "",
      shirtSize: data.shirt_size || "",
      youtubeUrl: primary?.youtube_url || "",
      appleUrl: primary?.apple_url || "",
      instagramUrl: primary?.instagram_url || "",
      facebookUrl: primary?.facebook_url || "",
      tiktokUrl: primary?.tiktok_url || "",
      soundcloudUrl: primary?.soundcloud_url || "",
      twitterUrl: primary?.twitter_url || "",
      amazonMusicUrl: primary?.amazon_music_url || "",
      deezerUrl: primary?.deezer_url || "",
      audiomackUrl: primary?.audiomack_url || "",
      pandoraUrl: primary?.pandora_url || "",
      tidalUrl: primary?.tidal_url || "",
      profilePic: data.profile_pic || "",
    },
    distributors: distributorList,
    countryCode: normalizedMobile.countryCode,
  };
};

